import { Scene, SceneLoader, AbstractMesh, AnimationGroup, Skeleton, Vector3, Bone, MeshBuilder, StandardMaterial, Color3, Quaternion, Matrix, MultiMaterial, VertexBuffer, VertexData, TransformNode, AssetContainer } from '@babylonjs/core';
import '@babylonjs/loaders/glTF';
import '@babylonjs/loaders';
import { AnimationPhase, AnimationMapping, AnimationSource, ModelAxesConfig, SubmeshDef } from '../config/characters';
import { logger } from '../core/Logger';
import { ServiceRegistry } from '../core/ServiceRegistry';
import { isEmbeddedReference, parseEmbeddedReference, ExtractionResult, getEntity, getEntityProperty, extractEntities } from './EntityAssetExtractor';
import { applyVideoMaterialToMesh, playVideoOnMesh, extractVideoConfigFromMesh } from './VideoTextureUtils';

export interface CharacterRenderFixes {
  flipFaces?: boolean;
  doubleSidedMaterials?: boolean;
  disableMaterials?: boolean;
  recomputeNormals?: boolean;
  /**
   * Forces `needDepthPrePass=true` for materials that appear to use alpha blending/testing.
   * Helps reduce transparency sorting artifacts (e.g. hair/eyelash cards drawing in wrong order).
   */
  transparentDepthPrePass?: boolean;
}

export interface SubmeshInstance {
  id: string;
  rootMesh: AbstractMesh;
  animations: Map<string, AnimationGroup>;
  animationMap?: Record<string, string>;
  debugMesh?: AbstractMesh;
  attachBone?: string;
}

export interface CharacterAsset {
  rootMesh: AbstractMesh;
  meshes: AbstractMesh[];
  skeleton: Skeleton | null;
  animations: Map<string, AnimationGroup>;
  // Map animation phases to actual animation groups
  phaseMap: Map<AnimationPhase, AnimationGroup>;
  submeshes: Map<string, SubmeshInstance>;
  // Embedded entities extracted from the GLB (for #entityId references)
  embeddedEntities?: ExtractionResult;
}

export class CharacterLoader {
  constructor(private scene: Scene) {}

  private toVector3(v: { x: number; y: number; z: number }): Vector3 {
    return new Vector3(v.x, v.y, v.z);
  }

  /**
   * Build a rotation that maps the model's declared forward/up axes to engine axes.
   * Engine convention here is: forward=+Z, up=+Y.
   */
  private computeModelAxesQuaternion(modelAxes: ModelAxesConfig): Quaternion {
    const fileForward = this.toVector3(modelAxes.forward);

    if (fileForward.lengthSquared() < 1e-6) {
      throw new Error('modelAxes.forward must be a non-zero vector');
    }
    const f = fileForward.normalize();

    // If up is not provided, pick any stable vector not colinear with forward.
    // (This is in model-local space; we only need a consistent orthonormal basis.)
    const fallbackUp = Math.abs(Vector3.Dot(f, Vector3.Up())) < 0.99 ? Vector3.Up() : Vector3.Right();
    const fileUp = modelAxes.up ? this.toVector3(modelAxes.up) : fallbackUp;

    if (fileUp.lengthSquared() < 1e-6) {
      throw new Error('modelAxes.up must be a non-zero vector (or omit it entirely)');
    }
    // Orthonormalize up against forward
    const uProj = f.scale(Vector3.Dot(fileUp, f));
    const u = fileUp.subtract(uProj);
    if (u.lengthSquared() < 1e-6) {
      throw new Error('modelAxes.up must not be colinear with modelAxes.forward (or omit it)');
    }
    const up = u.normalize();
    const right = Vector3.Cross(up, f).normalize();
    const forward = Vector3.Cross(right, up).normalize();

    // Basis matrix (columns: right, up, forward)
    const basis = Matrix.FromValues(
      right.x, right.y, right.z, 0,
      up.x, up.y, up.z, 0,
      forward.x, forward.y, forward.z, 0,
      0, 0, 0, 1
    );

    // We want rotation R such that R * basis = Identity => R = basis^-1.
    // For an orthonormal basis, inverse == transpose.
    const rot = basis.transpose();
    return Quaternion.FromRotationMatrix(rot);
  }

  private applyMaterialDoubleSided(material: unknown): void {
    if (!material) return;

    // MultiMaterial: apply to sub-materials.
    if (material instanceof MultiMaterial) {
      for (const sub of material.subMaterials) {
        this.applyMaterialDoubleSided(sub);
      }
      return;
    }

    const m: any = material;
    if (typeof m === 'object') {
      if ('backFaceCulling' in m) m.backFaceCulling = false;
      if ('twoSidedLighting' in m) m.twoSidedLighting = true;
    }
  }

  private applyTransparentDepthPrePass(material: unknown): void {
    if (!material) return;

    if (material instanceof MultiMaterial) {
      for (const sub of material.subMaterials) {
        this.applyTransparentDepthPrePass(sub);
      }
      return;
    }

    const m: any = material;
    try {
      const needsAlpha =
        (typeof m.needAlphaBlending === 'function' && m.needAlphaBlending()) ||
        (typeof m.needAlphaTesting === 'function' && m.needAlphaTesting()) ||
        (typeof m.alpha === 'number' && m.alpha < 1) ||
        !!m.opacityTexture ||
        (typeof m.transparencyMode === 'number' && m.transparencyMode !== 0);

      if (!needsAlpha) return;
      if ('needDepthPrePass' in m) m.needDepthPrePass = true;
    } catch {
      // ignore
    }
  }

  /**
   * Set maxSimultaneousLights on materials to match culling limit.
   * Gets the value from SpatialCullingManager if available, otherwise uses default of 8.
   */
  private ensureMaxLights(meshes: AbstractMesh[]): void {
    // Get maxLights from culling manager if available
    const culling = ServiceRegistry.getSpatialCulling();
    const maxLights = culling?.getMaxSimultaneousLights() ?? 8;
    
    for (const mesh of meshes) {
      if (!mesh.material) continue;
      
      const processMaterial = (mat: any) => {
        if (!mat) return;
        
        // Handle MultiMaterial
        if (mat.subMaterials) {
          for (const sub of mat.subMaterials) {
            processMaterial(sub);
          }
          return;
        }
        
        // Set maxSimultaneousLights to match culling limit
        if ('maxSimultaneousLights' in mat && mat.maxSimultaneousLights !== maxLights) {
          mat.maxSimultaneousLights = maxLights;
        }
      };
      
      processMaterial(mesh.material);
    }
  }

  private applyRenderFixes(meshes: AbstractMesh[], fixes?: CharacterRenderFixes): void {
    if (!fixes) return;
    if (
      !fixes.flipFaces &&
      !fixes.doubleSidedMaterials &&
      !fixes.disableMaterials &&
      !fixes.recomputeNormals &&
      !fixes.transparentDepthPrePass
    )
      return;

    for (const mesh of meshes) {
      if (fixes.disableMaterials) {
        mesh.material = null;
      }

      if (fixes.recomputeNormals) {
        const anyMesh: any = mesh;
        const positions = anyMesh.getVerticesData?.(VertexBuffer.PositionKind) as number[] | Float32Array | undefined;
        const indices = anyMesh.getIndices?.() as number[] | Int32Array | Uint16Array | Uint32Array | undefined;

        if (positions && indices && positions.length >= 3 && indices.length >= 3) {
          const normals = new Array<number>(positions.length).fill(0);
          try {
            VertexData.ComputeNormals(
              positions as any,
              indices as any,
              normals
            );
            anyMesh.setVerticesData?.(VertexBuffer.NormalKind, normals, true);
          } catch (error) {
            logger.assets.warn('Failed to recompute normals for mesh', {
              meshName: mesh.name,
              error,
            });
          }
        }
      }

      if (fixes.doubleSidedMaterials) {
        this.applyMaterialDoubleSided(mesh.material);
      }

      if (fixes.transparentDepthPrePass) {
        this.applyTransparentDepthPrePass(mesh.material);
      }

      if (fixes.flipFaces) {
        const anyMesh: any = mesh;
        if (typeof anyMesh.flipFaces === 'function') {
          try {
            anyMesh.flipFaces(true);
          } catch (error) {
            logger.assets.warn('Failed to flip faces for mesh', {
              meshName: mesh.name,
              error,
            });
          }
        }
      }
    }
  }

  /**
   * Load a character from FBX or GLTF file
   * @param characterName - Name for the character instance
   * @param filePath - Path to the asset file (FBX or GLTF)
   * @param animationMapping - Optional: Map animation clip names to phases
   * @param modelAxesOrRotationOffset - Optional: model axes config (preferred) or legacy yaw offset
   * @param submeshDefs - Optional: List of submeshes to load and attach
   * @param renderFixes - Optional: Render fixes to apply
   * @param forceEntityExtraction - Optional: Force entity extraction even if no embedded refs detected
   */
  async loadCharacter(
    characterName: string,
    filePath: string,
    animationMapping?: AnimationMapping,
    modelAxesOrRotationOffset?: ModelAxesConfig | number,
    submeshDefs?: SubmeshDef[],
    renderFixes?: CharacterRenderFixes,
    forceEntityExtraction: boolean = false
  ): Promise<CharacterAsset> {
    logger.assets.log(`Loading character: ${characterName} from ${filePath}`);

    // Check if any submesh or animation uses embedded references - if so, we need entity extraction
    const hasEmbeddedSubmeshes = submeshDefs?.some(def => isEmbeddedReference(def.source)) ?? false;
    const hasEmbeddedAnimations = animationMapping 
      ? Object.values(animationMapping).some(source => 
          typeof source !== 'string' && isEmbeddedReference(source.file)
        )
      : false;
    const needsEntityExtraction = hasEmbeddedSubmeshes || hasEmbeddedAnimations || forceEntityExtraction;
    
    logger.assets.log(`  Entity extraction: ${needsEntityExtraction} (submeshes=${hasEmbeddedSubmeshes}, anims=${hasEmbeddedAnimations}, forced=${forceEntityExtraction})`);
    
    let result: {
      meshes: AbstractMesh[];
      skeletons: Skeleton[];
      animationGroups: AnimationGroup[];
    };
    let embeddedEntities: ExtractionResult | undefined;
    let container: AssetContainer | undefined;

    try {
      if (needsEntityExtraction) {
        // Use AssetContainer to support entity extraction
        container = await SceneLoader.LoadAssetContainerAsync('', filePath, this.scene);
        
        // Extract entities from the container
        embeddedEntities = extractEntities(container);
        const entityIds = Array.from(embeddedEntities.entities.keys());
        logger.assets.log(`  Extracted ${embeddedEntities.entities.size} embedded entities: [${entityIds.join(', ')}]`);
        
        // Log all entity types for debugging
        for (const [id, entity] of embeddedEntities.entities) {
          logger.assets.log(`    Entity '${id}': type='${entity.type}', root='${entity.root.name}', meshes=${entity.meshes.length}`);
        }
        
        // Add everything to the scene first
        container.addAllToScene();
        
        // Hide template meshes for embedded projectile/weapon entities AFTER addAllToScene
        // These are templates that will be cloned when used as submeshes
        for (const entity of embeddedEntities.entities.values()) {
          if (entity.type === 'projectile' || entity.type === 'weapon') {
            // Hide the root node itself
            entity.root.setEnabled(false);
            if (entity.root instanceof AbstractMesh) {
              (entity.root as AbstractMesh).isVisible = false;
            }
            // Hide ALL descendant meshes (including deeply nested ones)
            for (const mesh of entity.root.getChildMeshes(false)) {
              mesh.isVisible = false;
              mesh.setEnabled(false);
            }
            logger.assets.debug(`  Hidden template for embedded ${entity.type}: ${entity.id} (root + ${entity.root.getChildMeshes(false).length} child meshes)`);
          }
        }
        
        result = {
          meshes: container.meshes,
          skeletons: container.skeletons,
          animationGroups: container.animationGroups,
        };
      } else {
        // Standard import (existing behavior)
        const importResult = await SceneLoader.ImportMeshAsync('', '', filePath, this.scene);
        if (!importResult) {
          throw new Error('SceneLoader.ImportMeshAsync returned no result');
        }
        result = importResult;
      }
    } catch (error) {
      logger.assets.error('Failed to import character base asset', {
        characterName,
        filePath,
        error,
      });
      throw error;
    }

    // Video textures now handled by video_material behavior - no legacy scan

    logger.assets.debug(`Loaded ${result.meshes.length} meshes, ${result.skeletons.length} skeletons, ${result.animationGroups.length} animations`);
    
    // Log mesh details (debug level)
    logger.assets.debug(`Mesh details from ${filePath}:`);
    result.meshes.forEach((mesh, idx) => {
      const vertices = mesh.getTotalVertices ? mesh.getTotalVertices() : 0;
      const hasMaterial = !!mesh.material;
      logger.assets.debug(`  ${idx}. "${mesh.name}" - ${vertices} vertices, material: ${hasMaterial}`);
    });
    
    // Log all animation names found
    if (result.animationGroups.length === 0) {
      logger.assets.warn(`No animations found in ${filePath}`);
    }

    // Find root mesh (usually the first one or one with children)
    const rootMesh = result.meshes.find(m => m.parent === null) || result.meshes[0];
    rootMesh.name = characterName;
    
    // Reset any imported quaternion so we control orientation deterministically.
    if (rootMesh.rotationQuaternion) {
      rootMesh.rotationQuaternion = null;
    }
    rootMesh.rotation.set(0, 0, 0);

    // Apply orientation if provided
    if (modelAxesOrRotationOffset !== undefined) {
      if (typeof modelAxesOrRotationOffset === 'number') {
        // Legacy: yaw-only rotation
        rootMesh.rotation.y = modelAxesOrRotationOffset;
        logger.assets.debug(
          `Applied legacy Y-axis rotation offset: ${modelAxesOrRotationOffset} radians`
        );
      } else {
        const q = this.computeModelAxesQuaternion(modelAxesOrRotationOffset);
        rootMesh.rotationQuaternion = q;
        rootMesh.rotation.set(0, 0, 0);
        logger.assets.debug(
          `Applied modelAxes orientation for ${characterName}`
        );
      }
    }

    // Apply optional render fixups (e.g., inside-out assets)
    if (renderFixes?.flipFaces || renderFixes?.doubleSidedMaterials) {
      this.applyRenderFixes(result.meshes, renderFixes);
      logger.assets.debug(
        `Applied renderFixes: flipFaces=${!!renderFixes.flipFaces}, doubleSidedMaterials=${!!renderFixes.doubleSidedMaterials}`
      );
    }

    // CRITICAL: Increase maxSimultaneousLights on character materials
    // By default Babylon.js limits to 4 lights, but we need more for dynamic spotlights
    this.ensureMaxLights(result.meshes);

    // Get skeleton (if any)
    const skeleton = result.skeletons.length > 0 ? result.skeletons[0] : null;
    if (skeleton) {
      logger.assets.debug(`Skeleton: ${skeleton.bones.length} bones`);
      
      // CRITICAL: Parent bone TransformNodes under rootMesh so they inherit character transforms
      for (const bone of skeleton.bones) {
        const tn = bone.getTransformNode();
        if (tn && !tn.parent) {
          tn.parent = rootMesh;
        }
      }
    }

    // Map animations by name and by phase
    const animations = new Map<string, AnimationGroup>();
    const phaseMap = new Map<AnimationPhase, AnimationGroup>();
    
    result.animationGroups.forEach((animGroup, _index) => {
      const animName = animGroup.name;
      
      logger.assets.debug(`Animation: "${animName}" (${animGroup.targetedAnimations.length} targets)`);
      
      // Stop all animations initially
      animGroup.stop();
      animGroup.normalize(0, animGroup.to);
      
      // Process root motion (lock X/Z) and extract natural speed
      const lowerName = animName.toLowerCase();
      const shouldPreserveRootMotion = lowerName.includes('attack') || lowerName.includes('turn');
      
      if (!shouldPreserveRootMotion) {
        const extractedSpeed = this.processInPlace(animGroup);
        if (extractedSpeed !== undefined) {
            animGroup.metadata = { ...animGroup.metadata, naturalSpeed: extractedSpeed };
        }
      }
      
      animations.set(animName, animGroup);
    });

    // Process animation mapping
    if (animationMapping) {
      for (const [phase, source] of Object.entries(animationMapping)) {
        if (typeof source === 'string') {
          // Source is animation name from main file
          const animGroup = animations.get(source);
          if (animGroup) {
            phaseMap.set(phase as AnimationPhase, animGroup);
            logger.assets.debug(`Mapped phase '${phase}' -> animation '${source}'`);
          } else {
            const available = Array.from(animations.keys()).join(', ');
            throw new Error(
              `❌ Animation '${source}' not found for phase '${phase}'\n` +
              `   Available animations in ${filePath}: ${available || '(none)'}`
            );
          }
        } else {
          // Source is separate file - will be loaded below
          logger.assets.debug(`Phase '${phase}' uses separate file: ${source.file}`);
        }
      }

      // Load animations from separate files OR embedded entities
      for (const [phase, source] of Object.entries(animationMapping)) {
        if (typeof source !== 'string') {
          const animSource = source as AnimationSource;
          try {
            logger.assets.debug(`Loading animation from ${animSource.file}...`);
            
            let animGroup: AnimationGroup;
            let shouldDisposeResult = false;
            let animResult: {
              meshes: AbstractMesh[];
              skeletons: Skeleton[];
              animationGroups: AnimationGroup[];
            } | undefined;
            
            if (isEmbeddedReference(animSource.file)) {
              // === EMBEDDED ENTITY ANIMATION ===
              const { entityId, animationName } = parseEmbeddedReference(animSource.file);
              
              if (!embeddedEntities) {
                throw new Error(
                  `Animation for phase '${phase}' uses embedded reference '${animSource.file}' but no entities were extracted. ` +
                  `Ensure the character GLB contains entities with hm_behaviors.`
                );
              }
              
              const entity = getEntity(embeddedEntities, entityId);
              
              // Use specified animation name, or from animSource.animationName
              const targetAnimName = animationName || animSource.animationName;
              
              if (targetAnimName) {
                const found = entity.animations.get(targetAnimName);
                if (!found) {
                  const available = Array.from(entity.animations.keys()).join(', ');
                  throw new Error(
                    `❌ Animation '${targetAnimName}' not found in entity '${entityId}'\n` +
                    `   Available animations: ${available || '(none)'}`
                  );
                }
                animGroup = found;
              } else {
                // No animation specified - use first one
                const firstAnim = entity.animations.values().next().value;
                if (!firstAnim) {
                  throw new Error(
                    `❌ No animations found in entity '${entityId}' for phase '${phase}'`
                  );
                }
                animGroup = firstAnim;
              }
              
              logger.assets.debug(`Using embedded animation '${animGroup.name}' from entity '${entityId}'`);
              
            } else {
              // === EXTERNAL FILE ANIMATION (existing behavior) ===
              try {
                animResult = await SceneLoader.ImportMeshAsync('', '', animSource.file, this.scene);
              } catch (error) {
                logger.assets.error('Failed to import animation asset', {
                  characterName,
                  phase,
                  filePath: animSource.file,
                  error,
                });
                throw error;
              }

              if (!animResult) {
                throw new Error('SceneLoader.ImportMeshAsync returned no result');
              }
              
              shouldDisposeResult = true;
              logger.assets.debug(`Loaded ${animResult.meshes.length} meshes, ${animResult.animationGroups.length} animations from ${animSource.file}`);
              
              if (animResult.animationGroups.length === 0) {
                throw new Error(
                  `❌ No animations found in ${animSource.file}\n` +
                  `   Expected at least one animation for phase '${phase}'`
                );
              }

              // Use specified animation or first one
              if (animSource.animationName) {
                const found = animResult.animationGroups.find(ag => ag.name === animSource.animationName);
                if (!found) {
                  const available = animResult.animationGroups.map(ag => ag.name).join(', ');
                  throw new Error(
                    `❌ Animation '${animSource.animationName}' not found in ${animSource.file}\n` +
                    `   Available animations: ${available}`
                  );
                }
                animGroup = found;
              } else {
                animGroup = animResult.animationGroups[0];
              }
            }

            // Retarget animation to our skeleton
            if (skeleton) {
              animGroup.stop();
              
              // Clone the animation group and retarget to main skeleton
              const clonedGroup = new AnimationGroup(
                `${phase}_${characterName}`,
                this.scene
              );
              
              // If mask is provided, filter bones
              let allowedBones: Set<string> | null = null;
              if (animSource.mask && animSource.mask.length > 0) {
                  const rootBones = animSource.mask;
                  allowedBones = new Set<string>();
                  
                  // Find all descendant bones for each root
                  for (const rootName of rootBones) {
                      const rootBone = skeleton.bones.find(b => b.name === rootName);
                      if (rootBone) {
                          this.collectDescendantBones(rootBone, allowedBones);
                      } else {
                          const availableBones = skeleton.bones.map(b => `  - ${b.name}`).join('\n');
                          throw new Error(
                            `❌ Mask bone '${rootName}' not found in skeleton for animation '${phase}'\n\n` +
                            `Available bones:\n${availableBones}`
                          );
                      }
                  }
                  logger.assets.debug(`Applied mask: ${allowedBones.size} bones included`);
              }

              for (const targetedAnim of animGroup.targetedAnimations) {
                const targetName = targetedAnim.target?.name;
                if (targetName) {
                  // Check mask
                  if (allowedBones && !allowedBones.has(targetName)) {
                      continue; // Skip this bone
                  }

                  // Find matching bone in our skeleton
                  const bone = skeleton.bones.find(b => b.name === targetName);
                  if (bone) {
                    const clonedAnim = targetedAnim.animation.clone();
                    
                    // For GLTF models, animations usually target the TransformNode linked to the bone
                    // If we target the Bone object directly, it might not work if the system expects TransformNode animation
                    const transformNode = bone.getTransformNode();
                    if (transformNode) {
                      clonedGroup.addTargetedAnimation(clonedAnim, transformNode);
                      // console.log(`        Mapped bone '${bone.name}' to TransformNode '${transformNode.name}'`);
                    } else {
                      // Fallback for non-GLTF or legacy skeletons
                      clonedGroup.addTargetedAnimation(clonedAnim, bone);
                      logger.assets.warn(`Could not find TransformNode for bone '${bone.name}', targeting bone directly`);
                    }
                  }
                }
              }
              
              // Set animation range
              clonedGroup.normalize(animGroup.from, animGroup.to);
              
              // Process root motion (lock X/Z) and extract natural speed
              // Skip if applyRootMotion is enabled or for 'turn' to preserve built-in movement/rotation
              let extractedSpeed: number | undefined;
              let startRootY: number | undefined;
              
              if (phase !== 'turn' && !animSource.applyRootMotion) {
                  const result = this.processInPlace(clonedGroup);
                  extractedSpeed = result.naturalSpeed;
                  startRootY = result.startRootY;
              }
              
              // Apply speed if specified
              if (animSource.speed) {
                  clonedGroup.speedRatio = animSource.speed;
              }
              
              // Store metadata (speed, blend config, mirror, mask, naturalSpeed, startRootY, phases, exitTime)
              // Prefer manually configured naturalSpeed, fall back to extracted
              // null means explicitly disable speed matching, undefined means use auto-detected
              const naturalSpeed = animSource.naturalSpeed !== undefined ? animSource.naturalSpeed : extractedSpeed;
              
              clonedGroup.metadata = { 
                  ...clonedGroup.metadata, 
                  defaultSpeed: animSource.speed || 1.0,
                  blendConfig: animSource.blend,
                  mirror: animSource.mirror,
                  mask: animSource.mask,
                  maskBones: allowedBones ? Array.from(allowedBones) : undefined,
                  naturalSpeed: naturalSpeed,
                  startRootY: startRootY,
                  phases: animSource.phases,       // Phase timing (windup/active/release/recovery)
                  exitTime: animSource.exitTime,   // Exit time configuration
                  startOffset: animSource.startOffset,
                  applyRootMotion: animSource.applyRootMotion,
                  lockFacing: animSource.lockFacing,
                  loopRange: animSource.loopRange, // Loop range for channeled intro/loop/outro
              };

              phaseMap.set(phase as AnimationPhase, clonedGroup);
              animations.set(`${phase}_external`, clonedGroup);
              logger.assets.debug(`Loaded animation for phase '${phase}' from ${animSource.file} (${clonedGroup.targetedAnimations.length} targets)`);
            } else {
              logger.assets.warn(`No skeleton found - cannot retarget animation from ${animSource.file}`);
            }

            // Clean up meshes AND skeletons from animation file (we only need the animation data)
            // Only for external files - embedded entities are part of the character container
            if (shouldDisposeResult && animResult) {
              animResult.meshes.forEach(mesh => mesh.dispose());
              animResult.skeletons.forEach(skel => skel.dispose());
            }
          } catch (error) {
            logger.userError(`Failed to load animation '${phase}' from ${animSource.file} for character '${characterName}'`);
            logger.assets.error('Failed to load/retarget animation', {
              characterName,
              phase,
              filePath: animSource.file,
              error,
            });
            throw error; // NO FALLBACK - re-throw
          }
        }
      }
    }

    // Load submeshes
    const submeshes = new Map<string, SubmeshInstance>();
    if (submeshDefs && skeleton) {
        for (const def of submeshDefs) {
            const submesh = await this.loadSubmesh(def, skeleton, rootMesh, embeddedEntities);
            if (submesh) {
                submeshes.set(def.id, submesh);
            }
        }
    }

    return {
      rootMesh,
      meshes: result.meshes,
      skeleton,
      animations,
      phaseMap,
      submeshes,
      embeddedEntities,
    };
  }

  private collectDescendantBones(bone: Bone, set: Set<string>) {
      set.add(bone.name);
      for (const child of bone.children) {
          this.collectDescendantBones(child, set);
      }
  }

  private async loadSubmesh(
    def: SubmeshDef, 
    parentSkeleton: Skeleton, 
    parentMesh: AbstractMesh,
    embeddedEntities?: ExtractionResult
  ): Promise<SubmeshInstance | null> {
    try {
      logger.assets.debug(`Loading submesh '${def.id}' from ${def.source}...`);
      
      let root: AbstractMesh;
      let allMeshes: AbstractMesh[];
      let animationGroups: AnimationGroup[];
      let effectiveAttachBone = def.attachBone;
      
      if (isEmbeddedReference(def.source)) {
        // === EMBEDDED ENTITY PATH ===
        const { entityId } = parseEmbeddedReference(def.source);
        
        if (!embeddedEntities) {
          throw new Error(
            `Submesh '${def.id}' uses embedded reference '${def.source}' but no entities were extracted from the character GLB`
          );
        }
        
        const entity = getEntity(embeddedEntities, entityId);
        logger.assets.debug(`Found embedded entity '${entityId}' with ${entity.meshes.length} meshes, ${entity.animations.size} animations`);
        
        // Get attach bone from entity extras (can override def.attachBone)
        const entityAttachBone = getEntityProperty<string | undefined>(entity.extras, 'attach_bone', undefined);
        if (entityAttachBone) {
          effectiveAttachBone = entityAttachBone;
          logger.assets.debug(`Using attach bone '${entityAttachBone}' from entity extras`);
        }
        
        if (!effectiveAttachBone) {
          throw new Error(
            `Submesh '${def.id}' (entity '${entityId}') has no attach bone specified. ` +
            `Set hm_attach_bone in Blender or provide attachBone in config.`
          );
        }
        
        // Clone the entity root and all children
        // We clone because the same entity might be used multiple times
        const clonedRoot = entity.root.clone(`submesh_${def.id}`, null, false);
        if (!clonedRoot) {
          throw new Error(`Failed to clone entity root for submesh '${def.id}'`);
        }
        
        // If root is not a mesh, find the first mesh child
        if (clonedRoot instanceof AbstractMesh) {
          root = clonedRoot;
        } else {
          const meshChild = clonedRoot.getChildMeshes(false)[0];
          if (!meshChild) {
            throw new Error(`Entity '${entityId}' has no meshes`);
          }
          root = meshChild;
          // Re-parent root mesh under the cloned transform
          root.parent = clonedRoot;
        }
        
        allMeshes = clonedRoot instanceof AbstractMesh ? [clonedRoot] : [];
        allMeshes.push(...clonedRoot.getChildMeshes(false));
        
        // Clone animation groups targeting this entity
        animationGroups = [];
        for (const [animName, animGroup] of entity.animations) {
          const clonedAnim = animGroup.clone(`${def.id}_${animName}`);
          animationGroups.push(clonedAnim);
        }
        
      } else {
        // === EXTERNAL FILE PATH (existing behavior) ===
        const result = await SceneLoader.ImportMeshAsync('', '', def.source, this.scene);
        
        // Find root (first mesh that has no parent, or just the first mesh)
        root = result.meshes.find(m => !m.parent) || result.meshes[0];
        root.name = `submesh_${def.id}`;
        allMeshes = result.meshes;
        animationGroups = result.animationGroups;
        
        if (!effectiveAttachBone) {
          throw new Error(
            `Submesh '${def.id}' has no attach bone specified. Provide attachBone in config.`
          );
        }
      }

      // Find the bone
      const bone = parentSkeleton.bones.find(b => b.name === effectiveAttachBone);
      if (!bone) {
          const availableBones = parentSkeleton.bones.map(b => `  - ${b.name}`).join('\n');
          const errorMsg = `Bone '${effectiveAttachBone}' not found for submesh '${def.id}'\n\nAvailable bones:\n${availableBones}`;
          logger.userError(errorMsg);
          throw new Error(errorMsg);
      }
      
      // Create anchor TransformNode and attach to bone using Babylon's attachToBone()
      // attachToBone is the ONLY way to properly follow animated bones
      const anchor = new TransformNode(`${def.id}_anchor`, this.scene);
      anchor.attachToBone(bone, parentMesh);
      
      // Normalize bone scaling on anchor
      const boneMatrix = bone.getAbsoluteTransform();
      const boneScale = new Vector3();
      boneMatrix.decompose(boneScale, undefined, undefined);
      if (boneScale.lengthSquared() > 0.0001) {
        const normalizationFactor = new Vector3(1/boneScale.x, 1/boneScale.y, 1/boneScale.z);
        anchor.scaling.copyFrom(normalizationFactor);
      }
      
      // Parent weapon root to anchor
      root.parent = anchor;

      // IMPORTANT: Set visibility AFTER attachment to ensure it takes effect
      root.setEnabled(true);
      root.isVisible = true;
      allMeshes.forEach(m => {
        m.setEnabled(true);
        m.isVisible = true;
      });

      // Apply configured transforms from config (scale, rotationOffset, positionOffset)
      // DO NOT force to zero - use the actual config values!
      const configScale = def.scale ?? 1.0;
      root.scaling.setAll(configScale);
      
      root.rotationQuaternion = null;
      if (def.rotationOffset) {
        // Config rotation is in degrees, convert to radians
        root.rotation = new Vector3(
          (def.rotationOffset.x ?? 0) * Math.PI / 180,
          (def.rotationOffset.y ?? 0) * Math.PI / 180,
          (def.rotationOffset.z ?? 0) * Math.PI / 180
        );
      } else {
        root.rotation = Vector3.Zero();
      }
      
      if (def.positionOffset) {
        root.position = new Vector3(
          def.positionOffset.x ?? 0,
          def.positionOffset.y ?? 0,
          def.positionOffset.z ?? 0
        );
      } else {
        root.position = Vector3.Zero();
      }

      // Create debug sphere attached to weapon root (always visible for debugging)
      const debugSphere = MeshBuilder.CreateSphere(`weapon_mount_debug`, { diameter: 0.5 }, this.scene);
      debugSphere.id = `weapon_mount_debug_${def.id}`; // Make it searchable
      debugSphere.renderingGroupId = 1; // Draw on top
      const debugMat = new StandardMaterial(`weapon_mount_debug_mat`, this.scene);
      debugMat.diffuseColor = new Color3(1, 1, 0); // Bright yellow
      debugMat.emissiveColor = new Color3(1, 1, 0);
      debugMat.disableLighting = true;
      debugMat.wireframe = false;
      debugSphere.material = debugMat;
      debugSphere.isPickable = false;
      debugSphere.isVisible = true; // Visible by default
      debugSphere.layerMask = 0x20000000; // Debug layer (bit 29)
      debugSphere.receiveShadows = false;
      debugSphere.metadata = { isDebug: true, noShadows: true };
      
      // Parent debug sphere to anchor (same as weapon, so they're at same position)
      debugSphere.parent = anchor;
      debugSphere.position = Vector3.Zero();
      debugSphere.rotation = Vector3.Zero();
      debugSphere.scaling = Vector3.One();

      // Process animations
      const animations = new Map<string, AnimationGroup>();
      const availableAnimNames: string[] = [];
      animationGroups.forEach(ag => {
          ag.stop();
          animations.set(ag.name, ag);
          availableAnimNames.push(ag.name);
      });
      
      logger.assets.debug(`Found ${animations.size} animations in submesh '${def.id}': ${availableAnimNames.join(', ') || '(none)'}`);
      
      // Validate animationMap references - FAIL FAST on missing animations
      if (def.animationMap) {
          for (const [characterState, weaponAnimName] of Object.entries(def.animationMap)) {
              if (!animations.has(weaponAnimName)) {
                  throw new Error(
                    `CharacterLoader: Animation '${weaponAnimName}' referenced in animationMap['${characterState}'] ` +
                    `not found in submesh '${def.id}'. Available animations: [${availableAnimNames.join(', ')}]`
                  );
              }
          }
      }
      
      // Apply video material to meshes that have it configured in Blender (hm_materialVideo_* on mesh or material)
      // Check each mesh individually for video material config
      for (const mesh of allMeshes) {
        const videoConfig = extractVideoConfigFromMesh(mesh);
        if (videoConfig) {
          logger.assets.debug(`Applying video material "${videoConfig.src}" to submesh mesh '${mesh.name}'`);
          const videoTexture = applyVideoMaterialToMesh(mesh, videoConfig);
          if (videoTexture && (videoConfig.autoplay ?? true)) {
            playVideoOnMesh(mesh);
          }
        }
      }

      return {
          id: def.id,
          rootMesh: root,
          animations,
          animationMap: def.animationMap,
          debugMesh: debugSphere,
          attachBone: effectiveAttachBone
      };

    } catch (e) {
        logger.userError(`Failed to load submesh '${def.id}' from ${def.source}`, e);
        return null;
    }
  }

  /**
   * Process animation to ensure it plays in place (removes root motion)
   * Returns the calculated natural speed from root bone movement and start root Y
   */
  private processInPlace(animationGroup: AnimationGroup): { naturalSpeed?: number, startRootY?: number } {
    // Find the root bone/transform animation
    // Mixamo usually names it "mixamo.com" or "Hips"
    // We look for the animation targeting the Hips
    
    let naturalSpeed: number | undefined = undefined;
    let startRootY: number | undefined = undefined;
    
    for (const targetedAnim of animationGroup.targetedAnimations) {
      const targetName = targetedAnim.target.name;
      // Check for common root bone names
      if (targetName.includes('Hips') || targetName.includes('Root') || targetName === 'mixamo.com') {
        const animation = targetedAnim.animation;
        
        // Check if this is a position animation
        if (animation.targetProperty === 'position') {
            const keys = animation.getKeys();
            
            // Calculate natural speed from root motion BEFORE zeroing it out
            if (keys.length > 1) {
                const startPos = keys[0].value;
                const endPos = keys[keys.length - 1].value;
                
                // Store start Y
                startRootY = startPos.y;

                // Calculate horizontal distance traveled (ignore Y)
                const deltaX = endPos.x - startPos.x;
                const deltaZ = endPos.z - startPos.z;
                const horizontalDistance = Math.sqrt(deltaX * deltaX + deltaZ * deltaZ);
                
                // Calculate animation duration in seconds
                const startFrame = keys[0].frame;
                const endFrame = keys[keys.length - 1].frame;
                const durationFrames = endFrame - startFrame;
                const fps = 60; // Babylon default
                const durationSeconds = durationFrames / fps;
                
                // Calculate speed in units per second
                if (durationSeconds > 0 && horizontalDistance > 0.01) {
                    naturalSpeed = horizontalDistance / durationSeconds;
                    logger.assets.debug(`Root motion detected: ${horizontalDistance.toFixed(2)} units over ${durationSeconds.toFixed(2)}s = ${naturalSpeed.toFixed(2)} units/sec`);
                }
            }
            
            // Keep the Y position (height), but lock X and Z to the first frame's value
            if (keys.length > 0) {
                const startPos = keys[0].value.clone();
                
                for (const key of keys) {
                    key.value.x = startPos.x;
                    key.value.z = startPos.z;
                    // We keep Y (jumping/crouching)
                }
            }
        }
        
        // Check if this is a rotation animation (quaternion)
        if (animation.targetProperty === 'rotationQuaternion') {
            const keys = animation.getKeys();
            if (keys.length > 0) {
                const startRot = keys[0].value.clone();
                for (const key of keys) {
                    key.value.copyFrom(startRot);
                }
            }
        }
      }
    }
    
    return { naturalSpeed, startRootY };
  }

  /**
   * Create a clone of a loaded character asset
   * IMPORTANT: Clones animations and retargets them to the new instance's bones
   */
  public _cloneCharacter(source: CharacterAsset, newName: string): CharacterAsset {
    const rootMesh = source.rootMesh.clone(newName, null);
    if (!rootMesh) {
      throw new Error('Failed to clone character');
    }

    // Clone all child meshes
    const meshes: AbstractMesh[] = [rootMesh];
    source.meshes.forEach(mesh => {
      if (mesh !== source.rootMesh) {
        const cloned = mesh.clone(`${newName}_${mesh.name}`, rootMesh);
        if (cloned) meshes.push(cloned);
      }
    });

    // Find the cloned skeleton in the new mesh hierarchy
    const clonedSkeleton = rootMesh.skeleton;
    if (!clonedSkeleton) {
      throw new Error('Cloned mesh has no skeleton');
    }

    // Retarget all animations to the cloned skeleton's bones
    const animations = new Map<string, AnimationGroup>();
    const phaseMap = new Map<AnimationPhase, AnimationGroup>();
    
    // Helper to retarget an AnimationGroup to the new skeleton
    const retargetAnimationGroup = (original: AnimationGroup, targetSkeleton: Skeleton): AnimationGroup => {
      const clonedGroup = new AnimationGroup(
        `${original.name}_${newName}`,
        this.scene
      );
      
      // Copy metadata
      clonedGroup.metadata = original.metadata;
      
      // Retarget each animation to the corresponding bone in the new skeleton
      for (const targetedAnim of original.targetedAnimations) {
        const originalTargetName = targetedAnim.target?.name;
        if (originalTargetName) {
          // Find the corresponding bone in the cloned skeleton
          const bone = targetSkeleton.bones.find(b => b.name === originalTargetName);
          if (bone) {
            const clonedAnim = targetedAnim.animation.clone();
            const transformNode = bone.getTransformNode();
            if (transformNode) {
              clonedGroup.addTargetedAnimation(clonedAnim, transformNode);
            } else {
              clonedGroup.addTargetedAnimation(clonedAnim, bone);
            }
          }
        }
      }
      
      // Normalize to the same range as the original
      clonedGroup.normalize(original.from, original.to);
      clonedGroup.stop();
      
      return clonedGroup;
    };
    
    // Retarget all animations
    for (const [name, group] of source.animations) {
      animations.set(name, retargetAnimationGroup(group, clonedSkeleton));
    }
    
    // Retarget all phase-mapped animations
    for (const [phase, group] of source.phaseMap) {
      phaseMap.set(phase, retargetAnimationGroup(group, clonedSkeleton));
    }
    
    const submeshes = new Map(source.submeshes); // Shallow copy

    return {
      rootMesh,
      meshes,
      skeleton: clonedSkeleton,
      animations,
      phaseMap,
      submeshes
    };
  }
}
